import { el } from "../components/dom.js";
import { pushSupported, pushEnabled, enablePush, disablePush, sendTestPush, loadPushSettings, savePushSettings } from "../db/push.js";
import { PUSH_CATEGORIES } from "../utils/push-schedule.js";
import { listDiagnostics, clearDiagnostics, diagnosticsText } from "../utils/diagnostics.js";
import { getSyncState, syncNow, retryFailedOps, SYNC_EVENT } from "../db/sync.js";
import { formatDate } from "../utils/dates.js";
import { showAuthToast } from "../components/auth-ui.js";
import {
	getCurrentUser,
	getProfile,
	updateProfile,
	checkHandle,
	signOut,
	deleteAccount,
	HANDLE_RE,
} from "../db/auth.js";
import { exportData, importData, isBackup, backupCounts } from "../db/backup.js";
import { debounce } from "../utils/debounce.js";
import {
	magScreenHead,
	magGlass,
	magButton,
	magSectionHeader,
	MAG,
} from "../components/magazine/kit.js";
import { navigate, navigateBack } from "../main.js";

const BIO_MAX = 160;

// ---- inline magazine form primitives ---------------------------------------
// Account is the one screen with a real text-entry form, so it carries its own
// small set of magazine-styled inputs (the kit doesn't ship form controls).
// Everything is inline-styled, matching the rest of the magazine rollout.

function magInput(props = {}) {
	return el("input", {
		autocomplete: "off",
		...props,
		style: {
			width: "100%",
			boxSizing: "border-box",
			background: "rgba(0,0,0,.25)",
			border: `1px solid ${MAG.line}`,
			borderRadius: "11px",
			padding: "12px 13px",
			color: "#fff",
			fontFamily: "'Archivo', sans-serif",
			fontSize: "15px",
			fontWeight: "600",
			outline: "none",
			...(props.style || {}),
		},
	});
}

function magFieldLabelText(text) {
	return el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "9px",
			letterSpacing: ".12em", textTransform: "uppercase", color: MAG.dim,
			marginBottom: "6px",
		},
	}, text);
}

function magField(labelText, control, hint) {
	return el(
		"label",
		{ style: { display: "block", marginBottom: "14px" } },
		magFieldLabelText(labelText),
		control,
		hint || null,
	);
}

// A per-field hint line with neutral / ok / bad colour states.
function magHint(initial = "") {
	const node = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "11px",
			marginTop: "6px", color: MAG.dim,
		},
	}, initial);
	return {
		node,
		set(text, state) {
			node.textContent = text || "";
			node.style.color = state === "ok" ? MAG.lime : state === "bad" ? "#ff5a4a" : MAG.dim;
		},
	};
}

// Form-level message (server errors / success confirmations).
function magMessage() {
	const node = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "12px",
			marginBottom: "12px", display: "none",
		},
	});
	function show(text, kind) {
		if (!text) {
			node.style.display = "none";
			node.textContent = "";
			return;
		}
		node.textContent = text;
		node.style.display = "block";
		node.style.color = kind === "ok" ? MAG.lime : "#ff5a4a";
	}
	return {
		node,
		error: (text) => show(text, "bad"),
		ok: (text) => show(text, "ok"),
		clear: () => show("", null),
	};
}

// A glass "setting card" under an Anton section header.
function settingCard(title, ...children) {
	return el(
		"div",
		{ style: { marginBottom: "4px" } },
		magSectionHeader(title),
		magGlass(children.filter(Boolean), { style: { padding: "16px" } }),
	);
}

function noteText(text) {
	return el("p", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "600", fontSize: "12px",
			color: MAG.dim, lineHeight: "1.45", margin: "12px 0 0",
		},
	}, text);
}

// Trigger a browser download of an object as a pretty-printed JSON file.
function downloadJson(filename, obj) {
	const blob = new Blob([JSON.stringify(obj, null, 2)], {
		type: "application/json",
	});
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

export async function renderAccount(root) {
	const user = await getCurrentUser();
	if (!user) {
		navigate("/sign-in");
		return;
	}

	let profile;
	try {
		profile = await getProfile(user.id);
	} catch (err) {
		root.append(
			el(
				"div.view",
				{},
				magScreenHead("Account", { eyebrow: "Your account", onBack: () => navigateBack("/") }),
				magGlass(
					el("div", {
						style: {
							fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "14px",
							color: MAG.dim, lineHeight: "1.5",
						},
					}, `Couldn't load your profile: ${err.message}`),
					{ style: { padding: "20px", margin: "16px 0" } },
				),
			),
		);
		return;
	}

	const msg = magMessage();

	// --- profile fields ---
	const displayName = magInput({
		type: "text",
		value: profile.display_name ?? "",
		maxlength: 50,
		placeholder: "Your name",
	});

	const handle = magInput({
		type: "text",
		value: profile.handle ?? "",
		maxlength: 30,
		placeholder: "handle",
		autocapitalize: "none",
		spellcheck: false,
	});
	const handleHint = magHint("");

	// Live, debounced handle availability check. `handleOk` gates Save so we never
	// submit a taken/invalid handle.
	let handleOk = true;
	const runHandleCheck = debounce(async () => {
		const value = handle.value.trim().toLowerCase();
		if (value === (profile.handle ?? "")) {
			handleOk = true;
			handleHint.set("", null);
			return;
		}
		if (!HANDLE_RE.test(value)) {
			handleOk = false;
			handleHint.set("3–30 chars: a–z, 0–9, underscore", "bad");
			return;
		}
		handleHint.set("Checking…", null);
		try {
			const { available, reason } = await checkHandle(value, user.id);
			handleOk = available;
			handleHint.set(available ? "Available" : reason, available ? "ok" : "bad");
		} catch {
			// Don't block saving on a transient lookup failure; the DB unique
			// constraint is the real guard.
			handleOk = true;
			handleHint.set("", null);
		}
	}, 350);
	handle.addEventListener("input", runHandleCheck);

	const bio = magInput({
		value: profile.bio ?? "",
		placeholder: "One line about you (optional)",
		maxlength: BIO_MAX,
	});
	const bioHint = magHint(`${(profile.bio ?? "").length}/${BIO_MAX}`);
	const updateBioCount = () =>
		bioHint.set(`${bio.value.length}/${BIO_MAX}`, null);
	bio.addEventListener("input", updateBioCount);

	const save = magButton("Save changes", { variant: "primary", block: true });

	async function doSave() {
		msg.clear();
		const name = displayName.value.trim();
		const h = handle.value.trim().toLowerCase();
		if (!name) return msg.error("Display name can't be empty.");
		if (!HANDLE_RE.test(h))
			return msg.error("Handle must be 3–30 chars: a–z, 0–9, underscore.");
		if (!handleOk) return msg.error("Pick an available handle.");

		save.disabled = true;
		try {
			const updated = await updateProfile(user.id, {
				display_name: name,
				handle: h,
				bio: bio.value.trim() || null,
			});
			profile = updated;
			msg.ok("Saved.");
		} catch (err) {
			msg.error(friendlyProfileError(err));
		} finally {
			save.disabled = false;
		}
	}
	save.onclick = doSave;

	// --- account actions ---
	const signOutBtn = magButton("Sign out", {
		variant: "ghost",
		block: true,
		onClick: async () => {
			try {
				await signOut();
			} finally {
				navigate("/");
			}
		},
	});

	// Deleting an account removes a row from auth.users, which needs privileges
	// the browser client doesn't have (a security-definer RPC / service role).
	// Adding that is a SQL change deferred to a later phase; show it disabled
	// rather than wiring a button that can't work.
	// Deleting is permanent: two confirmations, the second one typed.
	const deleteBtn = magButton("Delete account", {
		variant: "danger",
		block: true,
		style: { marginTop: "10px" },
		onClick: async () => {
			msg.clear();
			if (!confirm("Delete your account and everything in it — your library, groups, history and settings — on every device? This can't be undone. (Tip: Export backup first.)")) return;
			const typed = prompt('Type DELETE to confirm.');
			if ((typed ?? "").trim().toUpperCase() !== "DELETE") {
				showAuthToast("Not deleted.");
				return;
			}
			deleteBtn.disabled = true;
			try {
				await deleteAccount();
				showAuthToast("Your account was deleted.");
				navigate("/sign-in");
			} catch (err) {
				deleteBtn.disabled = false;
				msg.error(err.message ?? "Could not delete the account.");
			}
		},
	});

	// --- data backup / restore ---
	const exportBtn = magButton("Export backup", {
		variant: "ghost",
		block: true,
		onClick: async () => {
			msg.clear();
			try {
				const data = await exportData();
				downloadJson(
					`media-tracker-backup-${new Date().toISOString().slice(0, 10)}.json`,
					data,
				);
				showAuthToast("Backup downloaded");
			} catch (err) {
				msg.error(err?.message || "Could not export. Try again.");
			}
		},
	});

	const fileInput = el("input", {
		type: "file",
		accept: "application/json,.json",
		style: { display: "none" },
		onchange: async (ev) => {
			const file = ev.target.files?.[0];
			ev.target.value = ""; // let the same file be picked again later
			if (!file) return;
			msg.clear();
			try {
				const data = JSON.parse(await file.text());
				if (!isBackup(data)) {
					throw new Error("That doesn't look like a MediaTracker backup file.");
				}
				const n = backupCounts(data);
				const ok = window.confirm(
					`Replace all local data on this device with this backup (${n} item${n === 1 ? "" : "s"})? This can't be undone.`,
				);
				if (!ok) return;
				const restored = await importData(data, { replace: true });
				showAuthToast(`Restored ${restored} item${restored === 1 ? "" : "s"}`);
				navigate("/");
			} catch (err) {
				msg.error(err?.message || "Could not import that file.");
			}
		},
	});
	const importBtn = magButton("Import backup", {
		variant: "ghost",
		block: true,
		style: { marginTop: "10px" },
		onClick: () => fileInput.click(),
	});

	root.append(
		el(
			"div.view",
			{ style: { paddingBottom: "32px" } },
			magScreenHead("Account", { eyebrow: "Your account", onBack: () => navigateBack("/") }),
			msg.node,

			settingCard(
				"Profile",
				magField("Display name", displayName),
				magField("Handle", handle, handleHint.node),
				magField("Bio", bio, bioHint.node),
				save,
			),

			settingCard(
				"Login",
				magFieldLabelText("Email"),
				el("div", {
					style: {
						fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "15px",
						color: "#fff",
					},
				}, user.email),
			),

			settingCard(
				"Data",
				magButton("Open History", {
					variant: "ghost",
					onClick: () => navigate("/history"),
				}),
				noteText("Everything you've added, started, finished, owned and rated — plus past releases and every notification."),
				exportBtn,
				importBtn,
				fileInput,
				noteText(
					"Export a JSON copy of your games, books, TV, anime, movies, comics, groups, rankings, history, followed creators and series subscriptions — or restore one. Importing replaces this device's local data.",
				),
			),

			phoneNotificationsCard(),

			settingCard(
				"Import from another app",
				noteText("Bring in your Goodreads, Letterboxd, IMDb, MyAnimeList or Trakt history. You review every match before anything is added, and the dates come along into History."),
				el("div", { style: { marginTop: "10px" } }, magButton("Import…", { onClick: () => navigate("/import") })),
			),

			syncCard(),

			diagnosticsCard(),

			settingCard(
				"Sources",
				noteText(
					"Catalog data comes from IGDB (games), Open Library (books), TVmaze and MyAnimeList (TV and anime), OMDb (movies) and Metron + Open Library (comics).",
				),
			),

			settingCard(
				"Account",
				signOutBtn,
				deleteBtn,
				noteText("Deleting removes your account and all of its data from the server and this device. Other devices are signed out when they next sync."),
			),
		),
	);
}

function friendlyProfileError(err) {
	const m = (err?.message || "").toLowerCase();
	if (m.includes("duplicate") || m.includes("unique"))
		return "That handle is already taken.";
	if (m.includes("profiles_handle_format"))
		return "Handle must be 3–30 chars: a–z, 0–9, underscore.";
	if (m.includes("profiles_display_name"))
		return "Display name must be 1–50 characters.";
	if (m.includes("profiles_bio")) return "Bio must be 160 characters or fewer.";
	return err?.message || "Could not save. Try again.";
}

// Diagnostics (roadmap #9): the on-device error/warning log, newest first, with
// Copy (to paste into a bug report) and Clear.
function diagnosticsCard() {
	const list = el("div", { style: { display: "flex", flexDirection: "column", gap: "6px", marginTop: "8px" } });
	const summary = el("p", { style: { margin: "0", fontFamily: "'Archivo', sans-serif", fontSize: "13px", color: "#cfcfd6" } });
	function paint() {
		const entries = listDiagnostics();
		summary.textContent = entries.length
			? `${entries.length} recent ${entries.length === 1 ? "entry" : "entries"} — the newest are below.`
			: "Nothing logged. Errors and sync warnings on this device show up here.";
		list.replaceChildren(
			...entries.slice(0, 8).map((e) =>
				el("div", {
					style: {
						fontFamily: "ui-monospace, monospace", fontSize: "11px", lineHeight: "1.4", color: e.level === "error" ? "#ff8a80" : "#cfcfd6",
						whiteSpace: "pre-wrap", wordBreak: "break-word", borderTop: "1px solid rgba(255,255,255,.1)", paddingTop: "6px",
					},
				}, `${e.at.slice(0, 16).replace("T", " ")} · ${e.text.split("\n")[0]}`),
			),
		);
	}
	paint();
	const copyBtn = magButton("Copy log", {
		variant: "ghost",
		onClick: async () => {
			try {
				await navigator.clipboard.writeText(diagnosticsText(`v${__APP_VERSION__}`));
				showAuthToast("Copied — paste it into your message.");
			} catch {
				showAuthToast("Couldn't copy on this browser.");
			}
		},
	});
	const clearBtn = magButton("Clear", {
		variant: "ghost",
		onClick: () => {
			clearDiagnostics();
			paint();
		},
	});
	return settingCard(
		"Diagnostics",
		summary,
		el("div", { style: { display: "flex", gap: "8px", marginTop: "10px" } }, copyBtn, clearBtn),
		list,
	);
}

// Sync (roadmap #23): what the top-bar dot means, in words, with Sync now and —
// when something gave up — Retry.
function syncCard() {
	const line = el("p", { style: { margin: "0", fontFamily: "'Archivo', sans-serif", fontSize: "13px", color: "#cfcfd6", lineHeight: "1.45" } });
	const retryBtn = magButton("Retry failed", {
		variant: "ghost",
		onClick: async () => {
			retryBtn.disabled = true;
			await retryFailedOps();
			await paint();
		},
	});
	const nowBtn = magButton("Sync now", {
		variant: "ghost",
		onClick: async () => {
			nowBtn.disabled = true;
			try {
				await syncNow();
				showAuthToast("Synced.");
			} finally {
				nowBtn.disabled = false;
				await paint();
			}
		},
	});
	async function paint() {
		const s = await getSyncState();
		if (!s.signedIn) line.textContent = "Signed out — everything stays on this device.";
		else if (!s.online) line.textContent = `Offline. ${s.pending} change${s.pending === 1 ? "" : "s"} will sync when you're back online.`;
		else if (s.failed) line.textContent = `${s.failed} change${s.failed === 1 ? "" : "s"} couldn't sync (see Diagnostics for why). ${s.pending ? `${s.pending} more waiting.` : ""}`;
		else if (s.pending) line.textContent = `${s.pending} change${s.pending === 1 ? "" : "s"} waiting to upload…`;
		else line.textContent = `Everything is synced${s.lastSyncAt ? ` (last checked ${formatDate(s.lastSyncAt)} ${new Date(s.lastSyncAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})` : ""}.`;
		retryBtn.hidden = !s.failed;
		retryBtn.disabled = false;
	}
	paint();
	window.addEventListener(SYNC_EVENT, () => line.isConnected && paint());
	return settingCard("Sync", line, el("div", { style: { display: "flex", gap: "8px", marginTop: "10px" } }, nowBtn, retryBtn));
}

// Phone notifications (roadmap #61, #62): on / off for this device, a test, and
// what to notify about — categories, the time of day, quiet hours, a digest.
function phoneNotificationsCard() {
	const body = el("div", {});
	const status = noteText("");
	const chip = (label, on, onClick) =>
		el("button", {
			type: "button",
			onclick: onClick,
			"aria-pressed": on ? "true" : "false",
			style: {
				fontFamily: "'Archivo', sans-serif", fontSize: "10px", letterSpacing: ".05em", textTransform: "uppercase",
				borderRadius: "999px", padding: "7px 12px", cursor: "pointer", fontWeight: on ? "900" : "800",
				color: on ? "#0b0b0f" : "#9a9aa2", background: on ? "#b6f23d" : "rgba(255,255,255,.07)", border: on ? "0" : "1px solid rgba(255,255,255,.14)",
			},
		}, label);
	const hourSelect = (value, onChange) => {
		const sel = el("select", {
			style: { background: "rgba(255,255,255,.06)", color: "#f5f5f7", border: "1px solid rgba(255,255,255,.14)", borderRadius: "8px", padding: "6px 8px", fontFamily: "'Archivo', sans-serif" },
		}, ...Array.from({ length: 24 }, (_, h) => el("option", { value: String(h), selected: h === value }, `${String(h).padStart(2, "0")}:00`)));
		sel.addEventListener("change", () => onChange(Number(sel.value)));
		return sel;
	};
	const row = (...kids) => el("div", { style: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginTop: "10px" } }, ...kids);

	function paint() {
		body.replaceChildren();
		if (!pushSupported()) {
			body.append(noteText("This browser can't receive notifications. The bell in the app still collects everything."));
			return;
		}
		const on = pushEnabled();
		const s = loadPushSettings();
		const update = async (patch) => {
			await savePushSettings({ ...s, ...patch });
			paint();
		};
		body.append(
			noteText(on
				? "On for this device: release days, new episodes and comics you follow arrive as phone notifications."
				: "Get a notification on this device when something you track comes out — not just the bell inside the app."),
			row(
				magButton(on ? "Turn off" : "Turn on", {
					variant: on ? "ghost" : "primary",
					onClick: async () => {
						status.textContent = on ? "Turning off…" : "Asking for permission…";
						try {
							if (on) await disablePush();
							else {
								const n = await enablePush();
								status.textContent = `On — ${n ?? 0} notification${n === 1 ? "" : "s"} lined up for the next two weeks.`;
							}
							if (on) status.textContent = "Off.";
						} catch (err) {
							status.textContent = err?.message ?? String(err);
						}
						paint();
					},
				}),
				on
					? magButton("Send a test", {
							variant: "ghost",
							onClick: async () => {
								try {
									await sendTestPush();
									status.textContent = "Sent — it should appear in a few seconds.";
								} catch (err) {
									status.textContent = err?.message ?? String(err);
								}
							},
						})
					: "",
			),
			status,
		);
		if (!on) return;
		body.append(
			noteText("Notify me about"),
			row(...PUSH_CATEGORIES.map((c) => chip(c.label, s.categories[c.value], () => update({ categories: { ...s.categories, [c.value]: !s.categories[c.value] } })))),
			row(noteText("At"), hourSelect(s.hour, (h) => update({ hour: h })), noteText("on the day")),
			row(
				chip(s.quiet ? "Quiet hours on" : "Quiet hours off", !!s.quiet, () => update({ quiet: s.quiet ? null : { from: 22, to: 8 } })),
				s.quiet ? hourSelect(s.quiet.from, (h) => update({ quiet: { ...s.quiet, from: h } })) : "",
				s.quiet ? noteText("to") : "",
				s.quiet ? hourSelect(s.quiet.to, (h) => update({ quiet: { ...s.quiet, to: h } })) : "",
			),
			row(chip(s.digest ? "Daily digest: one notification a day" : "One notification per release", s.digest, () => update({ digest: !s.digest }))),
		);
	}
	paint();
	return settingCard("Phone notifications", body);
}
